import {useState} from 'react'
import _ from "lodash"

import {useNavigate, useParams} from "react-router";
import {useMediaQuery} from "react-responsive";
import {useShallow} from "zustand/react/shallow";

import fetcher from "../../fetcher";
import {useFetchHook} from "../../hooks/useFetchHook";
import useEnterEffect from "../../hooks/useEnterEffect";
import useDidMountEffect from "../../hooks/useDidMountEffect";
import {usePricingsStore} from "../../stores/pricingsStore";
import {useModalsStore} from "../../stores/modalsStore";
import {useDefaultQuoteSettingsContext} from "../../contexts/DefaultQuoteSettingsContext";
import {mediaQueryForMobile} from "../HomePage/HomePage";
import {LocationModal} from "../DealPage/LocationModal";
import {DeskingModal} from "./DeskingModal";
import {DealModal} from "./DealModal";
import {
    getAboveBackFromPriceAdjust,
    getMsrpPercentage,
    getPriceAdjust,
    getPriceAdjustAfterMagnitude,
    getPriceAdjustFromMsrpPercentage,
    getPriceAdjustState,
    getSignedPriceAdjust,
    toMoneyNumber,
} from "./priceAdjust";

// The two fields Deal.get_cost() measures from (`invoice or msrp`) — editing
// either invalidates a percentage the user typed against the old numbers.
const PERCENTAGE_BASE_FIELDS = ['msrp', 'invoice']

export const getInitialPricingRowState = (pricing, trim, quoteSettings) => {
    const customerLocation = quoteSettings.customer_location
    const dealerLocation = quoteSettings.dealer_location

    return {
        description: pricing?.description || '',
        msrp: pricing?.msrp || '',
        invoice: pricing?.invoice || null,
        above: pricing?.above || 0,
        back: pricing?.back || 0,
        profit: pricing?.profit || quoteSettings.profit,
        mileage: quoteSettings.mileage,
        mileage_alt: quoteSettings.mileage_alt,

        drive_off: quoteSettings.drive_off,
        car_id: trim.id,
        base_car_id: trim.id,
        base_car: trim.id,
        is_purchase: false,
        pricing_id: pricing?.id,
        pricing: pricing?.id,
        option_code_ids: [],
        rebates: [],

        address_line_one: customerLocation.address_line_one,
        address_line_two: customerLocation.address_line_two,
        city: customerLocation.city,
        state: customerLocation.state,
        zipcode: customerLocation.zipcode,
        county: customerLocation.county,

        dealer_address_line_one: dealerLocation.address_line_one,
        dealer_address_line_two: dealerLocation.address_line_two,
        dealer_city: dealerLocation.city,
        dealer_state: dealerLocation.state,
        dealer_zipcode: dealerLocation.zipcode,
        dealer_county: dealerLocation.county,
    }
}

// Compare money by value, not representation: '1000' vs 1000 must not mark an
// untouched row dirty. Blank stays distinct from zero — `invoice` is nullable.
const toComparableMoney = (value) =>
    (value === null || value === undefined || value === '') ? null : toMoneyNumber(value)

const hasMoneyChanged = (stateValue, pricingValue) =>
    toComparableMoney(stateValue) !== toComparableMoney(pricingValue)

// react-number-format reports a programmatic `value` prop change through the
// same onChange a keystroke uses, tagged `source: 'prop'`. Each box here renders
// a value derived from the other, so those echoes would hand ownership of the
// row to a field the user never touched. Only 'prop' is rejected — a caller with
// no sourceInfo at all is a real edit.
const isPropEcho = (sourceInfo) => sourceInfo?.source === 'prop'

// All state + behavior for a single pricing row, shared by the desktop
// (`PricingListRow`, table cells) and mobile (`PricingListRowMobile`, card)
// presentations so the two can never drift apart.
export const usePricingRow = ( { trim, pricing } ) => {
    const { quoteSettings } = useDefaultQuoteSettingsContext()
    const params = useParams();
    const navigate = useNavigate();
    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })
    const default_location = quoteSettings.customer_location
    const [dealLocation, setDealLocation] = useState(default_location)

    const [pricingState, setPricingState] = useState(getInitialPricingRowState(pricing, trim, quoteSettings))
    // What the control shows. `pricingState.above` / `.back` remain the source
    // of truth for the API; this exists so a zero magnitude can still remember
    // which sign the user picked.
    const [priceAdjust, setPriceAdjust] = useState(getPriceAdjustState(pricing?.above, pricing?.back))
    // What the user last typed into the % off MSRP box. `null` means "nothing
    // typed" and the box falls back to the value derived from the row itself, so
    // the percentage never becomes a second stored copy of the adjustment.
    const [typedMsrpPercentage, setTypedMsrpPercentage] = useState(null)
    const [isCreatingLease, setIsCreatingLease] = useState(false)
    const [isCreatingPurchase, setIsCreatingPurchase] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [isUpdating, setIsUpdating] = useState(false)

    const fetchPricings = usePricingsStore((state) => state.fetchPricings)

    const [createPricingResponse, createPricing] = useFetchHook(fetcher.createPricing)
    const [deletePricingResponse, deletePricing] = useFetchHook(fetcher.deletePricing)
    const [updatePricingResponse, updatePricing] = useFetchHook(fetcher.updatePricing)
    const [createDealResponse, createDeal] = useFetchHook(fetcher.createDeal)

    const {
        openModal,
        closeModalById,
    } = useModalsStore(
        useShallow((state) => ({
            openModal: state.openModal,
            closeModalById: state.closeModalById,
        }))
    )

    // `newDealerLocation`: the modal can stage a different Dealer pick in the same call.
    const saveLocation = (location, shouldSetCustomerLocation, shouldSetDealerLocation, newDealerLocation = location) => {
        let updateObj = {}

        if (shouldSetCustomerLocation) {
            updateObj = {
                location: location,
                location_id: location.id,
                address_line_one: location.address_line_one,
                address_line_two: location.address_line_two,
                city: location.city,
                state: location.state,
                zipcode: location.zipcode,
                county: location.county,
            }
        }
        if (shouldSetDealerLocation) {
            updateObj = {
                ...updateObj,
                dealer_location: newDealerLocation,
                dealer_location_id: newDealerLocation.id,
                dealer_address_line_one: newDealerLocation.address_line_one,
                dealer_address_line_two: newDealerLocation.address_line_two,
                dealer_city: newDealerLocation.city,
                dealer_state: newDealerLocation.state,
                dealer_zipcode: newDealerLocation.zipcode,
                dealer_county: newDealerLocation.county
            }
        }
        if (!_.isEmpty(updateObj)) {
            setDealLocation(location)
            setPricingState({...pricingState, ...updateObj})
        }

        closeModalById("LocationModal")
    }

    const updatePricingState = (field, value) => {
        // Editing either base moves the cost the percentage describes, so a
        // typed value goes stale and must give way to a freshly derived one.
        if (PERCENTAGE_BASE_FIELDS.includes(field)) setTypedMsrpPercentage(null)

        setPricingState({...pricingState, [field]: value})
    }

    // Every Price Adjust edit writes through to the `above` / `back` pair the
    // API expects, so nothing downstream (update / create / desk) has to know
    // the control exists.
    const updatePriceAdjust = (nextPriceAdjust) => {
        const signedAdjust = getSignedPriceAdjust(nextPriceAdjust)

        // The dollar box just redefined the adjustment, so whatever percentage
        // the user typed earlier no longer describes this row.
        setTypedMsrpPercentage(null)
        setPriceAdjust(nextPriceAdjust)
        setPricingState((currentPricingState) => ({
            ...currentPricingState,
            ...getAboveBackFromPriceAdjust(signedAdjust)
        }))
    }

    // Picking a sign is an intent that survives an empty box: + / - stays lit
    // with a zero magnitude, and the next digit typed adopts that direction.
    const setPriceAdjustSign = (sign) => updatePriceAdjust({...priceAdjust, sign})

    const setPriceAdjustMagnitude = (magnitude, sourceInfo) => {
        // This box re-rendering is the percentage's edit reaching the display,
        // not an edit of its own.
        if (isPropEcho(sourceInfo)) return

        updatePriceAdjust(getPriceAdjustAfterMagnitude(priceAdjust, magnitude))
    }

    // % off MSRP is a third view of the same above/back value — never a second
    // adjustment stacked on it. Derived unless the user is actively typing.
    const msrpPercentage = typedMsrpPercentage ?? getMsrpPercentage(
        pricingState.msrp, pricingState.invoice, pricingState.above, pricingState.back
    ) ?? ''

    const setMsrpPercentage = (percentage, sourceInfo) => {
        // The derived percentage changes on every Price Adjust edit; feeding it
        // back re-armed the percentage and rewrote the typed dollars with the
        // rounded percentage's own — $1,234 typed, $1,235 saved.
        if (isPropEcho(sourceInfo)) return

        setTypedMsrpPercentage(percentage)

        const signedAdjust = getPriceAdjustFromMsrpPercentage(
            pricingState.msrp, pricingState.invoice, percentage
        )
        // Null means the entry can't define an adjustment (box cleared, or no
        // MSRP to measure off). Clearing the box must not wipe a committed
        // Price Adjust, so leave above / back exactly as they were.
        if (signedAdjust === null) return

        const aboveBack = getAboveBackFromPriceAdjust(signedAdjust)

        setPriceAdjust(getPriceAdjustState(aboveBack.above, aboveBack.back))
        setPricingState((currentPricingState) => ({...currentPricingState, ...aboveBack}))
    }

    const deletePricingRow = (pricingId) => {
        setIsDeleting(true)
        return deletePricing(pricingId).then(() => {
            setIsDeleting(false)
            fetchPricings(params.make)
        }).catch(() => {
            setIsDeleting(false)
        })
    }

    const hasPricingChanged = () => {
        return (
            pricingState.description !== pricing.description ||
            hasMoneyChanged(pricingState.msrp, pricing.msrp) ||
            hasMoneyChanged(pricingState.invoice, pricing.invoice) ||
            // Compare the net adjustment, not the decomposition: a row saved as
            // above=1000/back=400 is unchanged while it still reads +600.
            getPriceAdjust(pricingState.above, pricingState.back) !==
                getPriceAdjust(pricing.above, pricing.back) ||
            hasMoneyChanged(pricingState.profit, pricing.profit)
        )
    }

    const updatePricingFunc = (isPurchase) => {
        if (hasPricingChanged()) {
            setIsUpdating(true)
            let updateData = pricingState
            updateData.id = pricing.id
            return updatePricing(pricing.id, updateData).then((data) => {
                setIsUpdating(false)
                fetchDesking(isPurchase)
            }).catch(() => {
                setIsUpdating(false)
            })
        } else {
            fetchDesking(isPurchase)
        }
    }

    const createNewPricing = (isPurchase) => {
        return createPricing(pricingState).then((data) => {
            // Brand-new rows have no pricing_id in pricingState yet (it was
            // pricing?.id, undefined). Thread the id from the create response into
            // the desk so the resulting Deal is linked back to this pricing.
            const newPricingId = data?.data?.id
            fetchDesking(isPurchase, newPricingId)
            setPricingState(
                getInitialPricingRowState(null, trim, quoteSettings)
            )
            setPriceAdjust(getPriceAdjustState(0, 0))
            setTypedMsrpPercentage(null)
            fetchPricings(params.make)
        })
    }

    const fetchDesking = (isPurchase, pricingIdOverride) => {
        let createData = structuredClone(pricingState)
        createData['is_purchase'] = isPurchase

        let mileage = createData.mileage
        if (['Ford', 'Lincoln'].includes(params.make)) {
            mileage = createData.mileage_alt
        }

        // Use the freshly-created pricing's id when provided (new rows); otherwise
        // fall back to the one already on pricingState (existing rows).
        const resolvedPricingId = pricingIdOverride ?? createData.pricing_id

        openModal({
            element: DeskingModal,
            id: "DeskingModal",
            props: {
                onSave: (updateData) =>  {
                    // On mobile the DealPage must be a full route, not a modal
                    // overlay — navigate to /quotes/:id instead of opening DealModal.
                    if (isTabletOrMobile) {
                        navigate(`/quotes/${updateData.id}`)
                        return
                    }
                    openModal({
                        element: DealModal,
                        id: "DealModal",
                        props: {id: updateData.id}
                    })
                },
                isDeskingContext: false,
                ...createData,
                pricing_id: resolvedPricingId,
                pricing: resolvedPricingId,
                id: null,
                mileage: mileage
            }
        })
    }

    const buildId = (field) => {
        return pricing ? `PricingListRow-${field}-${pricing.id}` : `PricingListRow-create-${field}`
    }

    const isLoading = createDealResponse?.loading || createPricingResponse?.loading || updatePricingResponse?.loading
    const isDisabled = Boolean(!pricingState.msrp || isLoading)

    const openLocationModal = () => {
        openModal({
            element: LocationModal,
            id: "LocationModal",
            props: {
                customerLocation: {
                    county: pricingState.county,
                    city: pricingState.city,
                    state: pricingState.state,
                    zipcode: pricingState.zipcode,
                },
                dealerLocation: {
                    county: pricingState.dealer_county,
                    city: pricingState.dealer_city,
                    state: pricingState.dealer_state,
                    zipcode: pricingState.dealer_zipcode,
                },
                selectLocation: saveLocation,
            }
        })
    }

    // Lease (isPurchase=false) / Purchase (isPurchase=true): update-then-desk for
    // existing pricings, create-then-desk for a brand-new row.
    const deskPricing = (isPurchase) => {
        if (isLoading) return;
        if (pricing?.id) {
            updatePricingFunc(isPurchase)
        } else {
            createNewPricing(isPurchase)
        }
    }

    useEnterEffect(() => {
        const docId = document.activeElement.id
        const [_, action, activePricingId] = docId.split('-')

        if (!isDisabled) {
            if (activePricingId === (String(pricing?.id)) ) {
                updatePricingFunc(false)
            }
            if (
                action === 'create' &&
                Boolean(!pricing)
            ) {
                createNewPricing(false)
            }
        }
    }, [pricingState])

    useDidMountEffect(function updatePricingStateWhenQuoteSettingsChange(){
        if (quoteSettings) {
            const customerLocation = quoteSettings.customer_location
            const dealerLocation = quoteSettings.dealer_location
            setPricingState({
                ...pricingState,
                profit: pricingState?.profit || quoteSettings.profit,
                drive_off: quoteSettings.drive_off,
                mileage: quoteSettings.mileage,
                mileage_alt: quoteSettings.mileage_alt,

                location: quoteSettings.customer_location,
                location_id: quoteSettings.customer_location.id,
                address_line_one: customerLocation.address_line_one,
                address_line_two: customerLocation.address_line_two,
                city: customerLocation.city,
                state: customerLocation.state,
                zipcode: customerLocation.zipcode,
                county: customerLocation.county,

                dealer_location: quoteSettings.dealer_location,
                dealer_location_id: quoteSettings.dealer_location.id,
                dealer_address_line_one: dealerLocation.address_line_one,
                dealer_address_line_two: dealerLocation.address_line_two,
                dealer_city: dealerLocation.city,
                dealer_state: dealerLocation.state,
                dealer_zipcode: dealerLocation.zipcode,
                dealer_county: dealerLocation.county
            })
        }
    }, [quoteSettings])

    const isDefaultLocation = Boolean(dealLocation.id === default_location.id)
    const isCreating = isCreatingLease || isCreatingPurchase

    return {
        pricingState,
        updatePricingState,
        priceAdjust,
        setPriceAdjustSign,
        setPriceAdjustMagnitude,
        msrpPercentage,
        setMsrpPercentage,
        dealLocation,
        isDefaultLocation,
        deletePricingRow,
        isDeleting,
        deskPricing,
        openLocationModal,
        buildId,
        isLoading,
        isDisabled,
        isCreating,
        isCreatingLease,
        isCreatingPurchase,
    }
}
